import FullCalendar from '@fullcalendar/react';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';
import interactionPlugin from '@fullcalendar/interaction';
import DragIndicatorIcon from '@material-ui/icons/PanTool';
import { useSnackbar } from 'notistack';
import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { Card, Container, useMediaQuery, Typography, Box, Grid, Stack, CircularProgress, IconButton, Tooltip } from '@material-ui/core';
import ProductsPopover from '../../components/ProductsPopover';
import ServicesPopover from '../../components/ServicesPopover';
import axios from 'axios';
import moment from 'moment';
import { PATH_DASHBOARD } from '../../routes/paths';
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { CalendarStyle, CalendarToolbar } from '../../components/_dashboard/calendar';
import EstimateCalendarPopOver from './EstimateCalendarPopOver';
import DateEstimatesPopup from './DateEstimatesPopup';
import { COMPANY_NAME, REST_API_END_POINT } from 'src/constants/Defaultvalues';
import { useDispatch, useSelector } from '../../redux/store';
import { getEvents, closeModal, updateEvent, selectRange } from '../../redux/slices/calendar';
import { isAdminUser } from '../../utils/adminUserTypes';
import {
  getCalendarHeightForAppZoom,
  subscribeFullCalendarZoomRefresh,
  useCalendarLayoutSync,
} from 'src/utils/fullCalendarZoom';

export const STAGE_COLORS = {
  All: '#2196F3',
  Hold: '#e0cc10',
  New: '#2196F3',
  'In Progress': '#ED9455',
  'Awaiting Manager Approval': '#9C27B0',
  Completed: '#4caf50',
  Drafts: '#9e9e9e',
  Rejected: '#f44336',
  Void: '#f44336',
};

export const STAGE_LABELS = {
  All: 'All',
  Hold: 'Hold',
  New: 'New',
  'In Progress': 'In Progress',
  'Awaiting Manager Approval': 'Manager Approval',
  Completed: 'Completed',
  Drafts: 'Drafts',
  Rejected: 'Rejected',
  Void: 'Void',
};

const FILTER_TABS = [
  { key: 'All', color: STAGE_COLORS.All, label: 'All' },
  { key: 'Hold', color: STAGE_COLORS.Hold, label: 'Hold' },
  { key: 'New', color: STAGE_COLORS.New, label: 'New' },
  { key: 'In Progress', color: STAGE_COLORS['In Progress'], label: 'In Progress' },
  { key: 'Awaiting Manager Approval', color: STAGE_COLORS['Awaiting Manager Approval'], label: 'Manager Approval' },
  { key: 'Completed', color: STAGE_COLORS.Completed, label: 'Completed' },
  { key: 'Drafts', color: STAGE_COLORS.Drafts, label: 'Drafts' },
  { key: 'Rejected', color: STAGE_COLORS.Rejected, label: 'Rejected' },
];

const isEstimateJobAwaitingManager = (row) =>
  (Number(row.manager_approval_required) === 1 || row.manager_approval_required === true) &&
  Number(row.send_to_client) !== 1 &&
  row.send_to_client !== '1' &&
  ![4, 6, 10, 11].includes(Number(row.quotation_status));

/** Tab assignment aligned with EstimateJobs (getAllEstimateProjects). */
export const getEstimateCalendarStage = (row) => {
  if (Number(row.quotation_status) === 12) return 'Void';
  if (Number(row.is_draft) === 1) return 'Drafts';
  if (Number(row.is_rejected) === 1 || Number(row.quotation_status) === 6 || Number(row.quotation_status) === 11) return 'Rejected';
  if (Number(row.project_hold ?? row.hold) === 1) return 'Hold';
  const status = Number(row.estimate_job_status);
  if (status === 1 || status === 0) return 'New';
  if (status === 2) return 'In Progress';
  if (status === 3) return isEstimateJobAwaitingManager(row) ? 'Awaiting Manager Approval' : 'Completed';
  return 'New';
};

/** Resolve stage for estimate job cards/list rows (projects use `status` when `estimate_job_status` is absent). */
export const getEstimateJobStageFromRow = (row) =>
  getEstimateCalendarStage({
    ...row,
    estimate_job_status: row?.estimate_job_status ?? row?.status,
    project_hold: row?.project_hold ?? row?.hold,
  });

export const getEstimateJobTabDisplayLabel = (tab) => STAGE_LABELS[tab] || tab;

export const getEstimateJobStageLabel = (row) =>
  STAGE_LABELS[getEstimateJobStageFromRow(row)] || STAGE_LABELS.New;

export const getEstimateJobStageColor = (row) =>
  STAGE_COLORS[getEstimateJobStageFromRow(row)] || STAGE_COLORS.New;

/** Hold through In Progress: single scheduled date only (no install start/end range). */
export const usesEstimateSingleDateOnly = (stage) =>
  ['Hold', 'New', 'Drafts', 'In Progress'].includes(stage);

const hasScheduledDate = (row) => {
  const pe = row.participation_ends;
  return pe && pe !== '0' && pe !== 0 && Number(pe) > 0;
};

const getCalendarUnixDate = (row) => {
  if (hasScheduledDate(row)) {
    return Number(row.participation_ends);
  }
  return Number(row.timestamp);
};

export default function EstimateCalendar({ provided, showDragIcon = false }) {
  const dispatch = useDispatch();
  const user = JSON.parse(window.localStorage.getItem('user'));
  let userType = user?.isClient ? 3 : user?.user_type;
  let userId = user?.user_id;

  if (user.added_by_client && user.parent_user_type && user.parent_id) {
    userId = user.parent_id;
    userType = 3;
  }

  const explodedPermissions = user?.exploded_permissions;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const calendarLayoutSync = useCalendarLayoutSync();
  const calendarHeight = isMobile ? 'auto' : getCalendarHeightForAppZoom();
  void calendarLayoutSync;
  const calendarRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState(isMobile ? 'listWeek' : 'dayGridMonth');
  const { selectedRange } = useSelector((state) => state.calendar);
  const [open, setOpen] = useState(false);
  const [projectSelect, setProjectSelect] = useState([]);
  const [anchorOrigin, setAnchorOrigin] = useState({ top: 100, left: 150 });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterTab, setFilterTab] = useState('All');
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [openDatePopup, setOpenDatePopup] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateEstimates, setDateEstimates] = useState([]);
  const [productsPopoverOpen, setProductsPopoverOpen] = useState(false);
  const [productsPopoverAnchor, setProductsPopoverAnchor] = useState(null);
  const [productsPopoverData, setProductsPopoverData] = useState({ productNames: '', eventColor: '#1ccaff' });
  const [servicesPopoverOpen, setServicesPopoverOpen] = useState(false);
  const [servicesPopoverAnchor, setServicesPopoverAnchor] = useState(null);
  const [servicesPopoverData, setServicesPopoverData] = useState({ serviceNames: '', eventColor: '#1ccaff' });
  const [events, setEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(moment().format('YYYY-MM'));
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isMountedRef = useRef(true);

  useEffect(() => {
    dispatch(getEvents());
  }, [dispatch]);

  const getAllEstimates = async (monthToUse = null) => {
    try {
      if (!isMountedRef.current) return;
      setLoading(true);

      const hasPermission = explodedPermissions?.some((permission) =>
        [
          'Manage Estimate Calendar',
          'View Estimate Calendar',
          'View Estimate Jobs',
          'View Estimate as Admin',
        ].includes(permission)
      );

      const viewAsAdmin = explodedPermissions?.some((permission) =>
        ['View Estimate as Admin', 'View as admin'].includes(permission)
      );

      const month = monthToUse || currentMonth || moment().format('YYYY-MM');

      const res = await axios.post(`${REST_API_END_POINT}request/fetch-all-estimates-calendar`, {
        userType: { value: viewAsAdmin ? 1 : userType },
        userId: { value: userId },
        hasPermission,
        viewAsAdmin,
        currentMonth: month,
      });

      const rows = res.data?.results || [];
      const allEvents = rows.map((event) => {
        const stage = getEstimateCalendarStage(event);
        let eventStart;
        let eventEnd;
        if (event.installation_start_date && event.installation_end_date) {
          eventStart = moment.unix(Number(event.installation_start_date)).format('YYYY-MM-DD');
          eventEnd = moment.unix(Number(event.installation_end_date)).add(1, 'day').format('YYYY-MM-DD');
        } else {
          const displayUnix = getCalendarUnixDate(event);
          eventStart = moment.unix(displayUnix).format('YYYY-MM-DD');
          eventEnd = moment.unix(displayUnix).add(1, 'day').format('YYYY-MM-DD');
        }
        return {
          id: event.id,
          title: event.name,
          calendar_stage: stage,
          quotation_status: event.quotation_status,
          estimate_job_status: event.estimate_job_status,
          hold: event.hold,
          is_new_request: stage === 'New' && !hasScheduledDate(event),
          is_draft: event.is_draft,
          project_hold: event.project_hold,
          start: eventStart,
          end: eventEnd,
          display_date: eventStart,
          start_date: eventStart,
          participation_ends: hasScheduledDate(event) ? Number(event.participation_ends) : null,
          installation_start_date: event.installation_start_date ? Number(event.installation_start_date) : null,
          installation_end_date: event.installation_end_date ? Number(event.installation_end_date) : null,
          quotation_status: event.quotation_status,
          estimate_job_status: event.estimate_job_status,
          timestamp: Number(event.timestamp),
          allDay: true,
          estimate: true,
          description: event.description,
          doc_number: event.doc_number || event.est_doc_number,
          client_name: event.customer_name,
          community_name: event.community_name,
          customer_name: event.customer_name,
          product_name: event.product_name,
          service_name: event.service_name,
        };
      });

      let filteredEvents = allEvents;
      if (filterTab !== 'All') {
        filteredEvents = allEvents.filter((e) => e.calendar_stage === filterTab);
      }
      setEvents(filteredEvents);
    } catch (error) {
      console.log('getAllEstimates error', error);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    getAllEstimates(currentMonth);
  }, [filterTab, refresh, currentMonth]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (selectedDate && events.length > 0) {
      const estimatesForDate = events.filter((event) => {
        const start = moment(event.start).format('YYYY-MM-DD');
        const end = moment(event.end).subtract(1, 'day').format('YYYY-MM-DD');
        return selectedDate >= start && selectedDate <= end;
      });
      setDateEstimates(estimatesForDate);
    }
  }, [events, selectedDate]);

  useEffect(() => subscribeFullCalendarZoomRefresh(calendarRef), []);

  useEffect(() => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.changeView(isMobile ? 'listWeek' : 'dayGridMonth');
      setView(isMobile ? 'listWeek' : 'dayGridMonth');
    }
  }, [isMobile]);

  const handleClickToday = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.today();
      setDate(calendarApi.getDate());
    }
  };

  const handleChangeView = (newView) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.changeView(newView);
      setView(newView);
    }
  };

  const handleClickDatePrev = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.prev();
      setDate(calendarApi.getDate());
    }
  };

  const handleClickDateNext = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.next();
      setDate(calendarApi.getDate());
    }
  };

  const handleSelectRange = (arg) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) calendarEl.getApi().unselect();
    dispatch(selectRange(arg.start, arg.end));
  };

  const handleDateClick = (arg) => {
    const clickedDate = moment(arg.date).format('YYYY-MM-DD');
    setSelectedDate(clickedDate);
    const estimatesForDate = events.filter((event) => {
      const start = moment(event.start).format('YYYY-MM-DD');
      const end = moment(event.end).subtract(1, 'day').format('YYYY-MM-DD');
      return clickedDate >= start && clickedDate <= end;
    });
    setDateEstimates(estimatesForDate);
    setOpenDatePopup(true);
  };

  const handleSelectEvent = (arg) => {
    const clickedElement = arg.jsEvent.target;
    const isMoreText =
      clickedElement.textContent &&
      clickedElement.textContent.includes('+') &&
      clickedElement.textContent.includes('more') &&
      clickedElement.textContent.trim().match(/^\+\d+\s+more$/);
    const hasMoreClass = clickedElement.classList && clickedElement.classList.contains('more-text');
    const hasMoreDataAttr = clickedElement.getAttribute && clickedElement.getAttribute('data-more-text') === 'true';

    if (isMoreText || hasMoreClass || hasMoreDataAttr) {
      return;
    }

    const eventId = Number(arg.event.id);
    const eventData = events.find((v) => Number(v.id) === eventId);
    if (!eventData) {
      return;
    }

    const eventElement = arg.jsEvent.target;
    const rect = eventElement.getBoundingClientRect();
    setAnchorOrigin({ top: rect.top + window.scrollY, left: rect.left + window.scrollX });
    setSelectedEvent(arg.event);
    setProjectSelect(eventData);
    setOpen(true);
  };

  const handleDatesSet = (arg) => {
    const currentViewStart = arg?.view?.currentStart || arg.start;
    const newMonth = moment(currentViewStart).format('YYYY-MM');
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }
    if (newMonth !== currentMonth) setCurrentMonth(newMonth);
  };

  const handleDropEvent = async ({ event }) => {
    try {
      const isRejectedEstimate =
        event?._def?.extendedProps?.calendar_stage === 'Rejected' ||
        Number(event?._def?.extendedProps?.is_rejected) === 1 ||
        Number(event?._def?.extendedProps?.quotation_status) === 6;

      if (isRejectedEstimate) {
        enqueueSnackbar("You can't update date for rejected estimate", { variant: 'error' });
        getAllEstimates(currentMonth);
        return;
      }

      if (
        !isAdminUser (userType) &&
        !explodedPermissions?.some((permission) => permission === 'Drag And Drop Estimate Calendar')
      ) {
        enqueueSnackbar("You don't have necessary permission", { variant: 'error' });
        getAllEstimates(currentMonth);
        return;
      }

      const originalStartDate = event._def?.extendedProps?.installation_start_date;
      const originalEndDate = event._def?.extendedProps?.installation_end_date;
      let participationEnds = moment(event.start).unix();
      let installationStartDate = null;
      let installationEndDate = null;

      if (originalStartDate && originalEndDate) {
        const oldStart = moment.unix(originalStartDate);
        const newStart = moment(event.start);
        const originalStart = moment.unix(originalStartDate);
        const originalEnd = moment.unix(originalEndDate);
        const offsetDays = newStart.diff(oldStart, 'days');
        installationStartDate = originalStart.add(offsetDays, 'days').unix();
        installationEndDate = originalEnd.add(offsetDays, 'days').unix();
        participationEnds = installationEndDate;
      }

      const response = await axios.post(`${REST_API_END_POINT}request/update-estimate-calendar-date`, {
        request_id: event.id,
        participation_ends: participationEnds,
        installation_start_date: installationStartDate,
        installation_end_date: installationEndDate,
        added_by: user.user_id,
      });

      if (response.data.status === 1) {
        dispatch(
          updateEvent(event.id, {
            allDay: event.allDay,
            start: event.start,
            end: event.end,
          })
        );
        enqueueSnackbar('Estimate date updated successfully', { variant: 'success' });
      } else {
        enqueueSnackbar(response.data.message || 'Failed to update estimate date', { variant: 'error' });
      }
      getAllEstimates(currentMonth);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Error updating estimate date', { variant: 'error' });
    }
  };

  const getEventColor = (stage) => STAGE_COLORS[stage] || STAGE_COLORS.New;

  const renderEventContent = (eventInfo, stage) => {
    // Check if this is a true multi-day event (has installation_start_date and installation_end_date)
    const hasInstallationStartDate = eventInfo.event._def.extendedProps.installation_start_date;
    const hasInstallationEndDate = eventInfo.event._def.extendedProps.installation_end_date;
    const isTrueMultiDay = hasInstallationStartDate && hasInstallationEndDate;
    
    // Check if this is a multi-day event in FullCalendar
    const isMultiDay = eventInfo.event.allDay || 
      (eventInfo.event.start && eventInfo.event.end && 
       moment(eventInfo.event.end).diff(moment(eventInfo.event.start), 'days') > 0);
    
    // Determine if this is start, middle, or end of multi-day event
    const { isStart, isEnd } = eventInfo;
    const isMiddle = isMultiDay && !isStart && !isEnd;
    
    const eventColor = getEventColor(stage);

    // Determine border style based on event type and position
    const borderStyle = `2px solid ${eventColor}`;
    let borderRadius = '4px';
    
    // For true multi-day events (with installation_start_date and installation_end_date)
    if (isTrueMultiDay && isMultiDay) {
      if (isStart) {
        // Start day: right border open
        borderRadius = '4px 0 0 4px';
      } else if (isEnd) {
        // End day: left border open
        borderRadius = '0 4px 4px 0';
      } else if (isMiddle) {
        // Middle days: only top and bottom borders
        borderRadius = '0';
      }
    }
    // For single-day events (only installation_date), keep complete borders

    // Build the border styles object
    const borderStyles = {
      width: '100%',
      minHeight: '60px',
      border: borderStyle,
      borderRadius: borderRadius,
      backgroundColor: 'transparent',
      padding: '8px 10px',
    };

    // For true multi-day events, handle borders based on position
    if (isTrueMultiDay && isMultiDay) {
      if (isMiddle) {
        // Middle days: remove left and right borders
        borderStyles.borderLeft = 'none';
        borderStyles.borderRight = 'none';
        borderStyles.marginLeft = '-2px';
        borderStyles.marginRight = '-2px';
        borderStyles.borderRadius = '0'; // no radius in the middle
      } else if (isStart && !isEnd) {
        // Start day
        borderStyles.borderRight = 'none';
        borderStyles.marginRight = '-2px';
        borderStyles.borderRadius = '4px 0 0 4px'; // rounded only on left
      } else if (isEnd && !isStart) {
        // End day
        borderStyles.borderLeft = 'none';
        borderStyles.marginLeft = '-2px';
        borderStyles.borderRadius = '0 4px 4px 0'; // rounded only on right
      } else if (isStart && isEnd) {
        // Single-day event (both start & end)
        borderStyles.borderRadius = '4px'; // full radius
      }
    }    

    // Get product names and create display
    const productName = eventInfo.event._def.extendedProps.product_name;
    const products = productName ? productName.split(',').map(p => p.trim()).filter(p => p) : [];
    
    // Get service names and create display
    const serviceName = eventInfo.event._def.extendedProps.service_name;
    const services = serviceName ? serviceName.split(',').map(s => s.trim()).filter(s => s) : [];
    
    const renderProducts = () => {
      if (products.length === 0) return null;
      
      if (products.length <= 2) {
        return (
          <Box sx={{ mt: 0.5 }}>
            <Typography 
              color={eventColor} 
              fontSize="0.65rem" 
              sx={{ 
                fontWeight: 600, 
                opacity: 0.8,
                mb: 0.5
              }}
              variant="caption"
            >
              Product(s):
            </Typography>
            <Box
              sx={{
                backgroundColor: `${eventColor}15`,
                border: `1px solid ${eventColor}40`,
                borderRadius: '4px',
                padding: '6px 8px',
                minHeight: '30px'
              }}
            >
              <Stack spacing={0.5}>
                  {products.map((product, index) => (
                    <Tooltip 
                      key={index} 
                      title={product} 
                      arrow
                      PopperProps={{
                        sx: {
                          zIndex: 9999
                        }
                      }}
                    >
                      <Typography
                        color={eventColor}
                        fontSize="0.7rem"
                        sx={{
                          lineHeight: 1.2,
                          opacity: 0.9,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          cursor: 'help'
                        }}
                        variant="caption"
                      >
                        • {product}
                      </Typography>
                    </Tooltip>
                  ))}
              </Stack>
            </Box>
          </Box>
        );
      }
      
      // Show first 2 products + count more
      const firstTwoProducts = products.slice(0, 2);
      const remainingCount = products.length - 2;
      
      return (
        <Box sx={{ mt: 0.5 }}>
          <Typography 
            color={eventColor} 
            fontSize="0.65rem" 
            sx={{ 
              fontWeight: 600, 
              opacity: 0.8,
              mb: 0.5
            }}
            variant="caption"
          >
            Products:
          </Typography>
          <Box
            sx={{
              backgroundColor: `${eventColor}15`,
              border: `1px solid ${eventColor}40`,
              borderRadius: '4px',
              padding: '6px 8px',
              minHeight: '30px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: `${eventColor}25`,
                border: `1px solid ${eventColor}60`
              }
            }}
          >
            <Stack spacing={0.5}>
              {firstTwoProducts.map((product, index) => (
                <Tooltip 
                  key={index} 
                  title={product} 
                  arrow
                  PopperProps={{
                    sx: {
                      zIndex: 9999
                    }
                  }}
                >
                  <Typography
                    color={eventColor}
                    fontSize="0.7rem"
                    sx={{
                      lineHeight: 1.2,
                      opacity: 0.9,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      cursor: 'help'
                    }}
                    variant="caption"
                  >
                    • {product}
                  </Typography>
                </Tooltip>
              ))}
              <Typography
                color={eventColor}
                fontSize="0.7rem"
                sx={{
                  lineHeight: 1.2,
                  opacity: 0.9,
                  fontWeight: 600,
                  display: 'block',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  zIndex: 10,
                  '&:hover': {
                    opacity: 1,
                    textDecoration: 'underline !important'
                  }
                }}
                variant="caption"
                className="more-text"
                data-more-text="true"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  console.log('Clicked on +more text');
                  // Open products popover instantly
                  setProductsPopoverData({
                    productNames: eventInfo.event._def.extendedProps.product_name,
                    eventColor: eventColor
                  });
                  setProductsPopoverAnchor(e.currentTarget);
                  setProductsPopoverOpen(true);
                }}
              >
                +{remainingCount} more
              </Typography>
            </Stack>
          </Box>
        </Box>
      );
    };

    const renderServices = () => {
      if (services.length === 0) return null;
      
      if (services.length <= 2) {
        return (
          <Box sx={{ mt: 0.5 }}>
            <Typography 
              color={eventColor} 
              fontSize="0.65rem" 
              sx={{ 
                fontWeight: 600, 
                opacity: 0.8,
                mb: 0.5
              }}
              variant="caption"
            >
              Service(s):
            </Typography>
            <Box
              sx={{
                backgroundColor: `${eventColor}15`,
                border: `1px solid ${eventColor}40`,
                borderRadius: '4px',
                padding: '6px 8px',
                minHeight: '30px'
              }}
            >
              <Stack spacing={0.5}>
                  {services.map((service, index) => (
                    <Tooltip 
                      key={index} 
                      title={service} 
                      arrow
                      PopperProps={{
                        sx: {
                          zIndex: 9999
                        }
                      }}
                    >
                      <Typography
                        color={eventColor}
                        fontSize="0.7rem"
                        sx={{
                          lineHeight: 1.2,
                          opacity: 0.9,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          cursor: 'help'
                        }}
                        variant="caption"
                      >
                        • {service}
                      </Typography>
                    </Tooltip>
                  ))}
              </Stack>
            </Box>
          </Box>
        );
      }
      
      // Show first 2 services + count more
      const firstTwoServices = services.slice(0, 2);
      const remainingCount = services.length - 2;
      
      return (
        <Box sx={{ mt: 0.5 }}>
          <Typography 
            color={eventColor} 
            fontSize="0.65rem" 
            sx={{ 
              fontWeight: 600, 
              opacity: 0.8,
              mb: 0.5
            }}
            variant="caption"
          >
            Services:
          </Typography>
          <Box
            sx={{
              backgroundColor: `${eventColor}15`,
              border: `1px solid ${eventColor}40`,
              borderRadius: '4px',
              padding: '6px 8px',
              minHeight: '30px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: `${eventColor}25`,
                border: `1px solid ${eventColor}60`
              }
            }}
          >
            <Stack spacing={0.5}>
              {firstTwoServices.map((service, index) => (
                <Tooltip 
                  key={index} 
                  title={service} 
                  arrow
                  PopperProps={{
                    sx: {
                      zIndex: 9999
                    }
                  }}
                >
                  <Typography
                    color={eventColor}
                    fontSize="0.7rem"
                    sx={{
                      lineHeight: 1.2,
                      opacity: 0.9,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      cursor: 'help'
                    }}
                    variant="caption"
                  >
                    • {service}
                  </Typography>
                </Tooltip>
              ))}
              <Typography
                color={eventColor}
                fontSize="0.7rem"
                sx={{
                  lineHeight: 1.2,
                  opacity: 0.9,
                  fontWeight: 600,
                  display: 'block',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  zIndex: 10,
                  '&:hover': {
                    opacity: 1,
                    textDecoration: 'underline !important'
                  }
                }}
                variant="caption"
                className="more-text"
                data-more-text="true"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  console.log('Clicked on +more services text');
                  // Open services popover instantly
                  setServicesPopoverData({
                    serviceNames: eventInfo.event._def.extendedProps.service_name,
                    eventColor: eventColor
                  });
                  setServicesPopoverAnchor(e.currentTarget);
                  setServicesPopoverOpen(true);
                }}
              >
                +{remainingCount} more
              </Typography>
            </Stack>
          </Box>
        </Box>
      );
    };

    return (
        <Box sx={borderStyles}>
            <Tooltip 
                title={eventInfo.event.title} 
                arrow
                PopperProps={{
                    sx: {
                        zIndex: 9999
                    }
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 0.5 }}>
                    <Typography color={eventColor} variant='caption' sx={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        opacity: 0.8,
                        lineHeight: 1.2,
                        cursor: 'help',
                        mb: 0.2
                    }}>
                        Estimate name:
                    </Typography>
                    <Typography color={eventColor} variant='caption' sx={{
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.2,
                        cursor: 'help'
                    }}>
                        {eventInfo.event.title}
                    </Typography>
                </Box>
            </Tooltip>
            {/* Show additional top-level details (community, customer, id) if present */}
            {(() => {
              const community = eventInfo.event._def.extendedProps.community_name || eventInfo.event._def.extendedProps.community || null;
              const customer = eventInfo.event._def.extendedProps.customer_name || eventInfo.event._def.extendedProps.client_name || null;
              const evtId = eventInfo.event._def.extendedProps.doc_number || eventInfo.event.doc_number || null;
              return (community || customer || evtId) ? (
                <Box sx={{ mt: 0.5 }}>
                  {evtId && (
                    <Box>
                      <Typography color={eventColor} fontSize="0.65rem" sx={{ fontWeight: 600, opacity: 0.8 }} variant="caption">
                       Order ID:
                      </Typography>
                      <Typography color={eventColor} variant="caption" sx={{ display: 'block', fontSize: '0.75rem', lineHeight: 1.2 }}>
                        {evtId}
                      </Typography>
                    </Box>
                  )}
                  {community && (
                    <Box sx={{ mb: 0.4 }}>
                      <Typography color={eventColor} fontSize="0.65rem" sx={{ fontWeight: 600, opacity: 0.8 }} variant="caption">
                        Project:
                      </Typography>
                      <Tooltip title={community} arrow PopperProps={{ sx: { zIndex: 9999 } }}>
                        <Typography color={eventColor} variant="caption" sx={{ display: 'block', fontSize: '0.75rem', lineHeight: 1.2,maxWidth: 150,overflow: 'hidden',textOverflow: 'ellipsis',whiteSpace: 'nowrap' }}
                        onMouseEnter={() => console.log('Tooltip Hovered')}
                        onMouseLeave={() => console.log('Tooltip Left')}>
                          {community}
                        </Typography>
                      </Tooltip>
                    </Box>
                  )}
                  {customer && (
                    <Box sx={{ mb: 0.4 }}>
                      <Typography color={eventColor} fontSize="0.65rem" sx={{ fontWeight: 600, opacity: 0.8 ,maxWidth: 150,overflow: 'hidden',textOverflow: 'ellipsis',whiteSpace: 'nowrap' }} variant="caption">
                        Client:
                      </Typography>
                      <Tooltip title={customer} arrow>
                        <Typography color={eventColor} variant="caption" sx={{ display: 'block', fontSize: '0.75rem', lineHeight: 1.2,maxWidth: 150,overflow: 'hidden',textOverflow: 'ellipsis',whiteSpace: 'nowrap' }}>
                          {customer}
                        </Typography>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              ) : null;
            })()}
            {renderProducts()}
            {renderServices()}
        </Box>
    );
};


  return (
    <Page title={`${COMPANY_NAME}`}>
      <Container maxWidth="xl">
        <HeaderBreadcrumbs
          heading="Estimate Calendar"
          sx={{
            color: '#ffffff',
            '& .MuiTypography-root': { color: '#ffffff' },
            '& a': { color: '#ffffff' },
          }}
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Estimate Calendar' }]}
        />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack spacing={2} direction="row" flexWrap="wrap" sx={{ p: 2 }}>
              {FILTER_TABS.map((tab) => (
                <Box
                  key={tab.key}
                  sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setFilterTab(tab.key)}
                >
                  <Box sx={{ width: 20, height: 20, backgroundColor: tab.color, borderRadius: 0.5, opacity: filterTab === tab.key ? 1 : 0.5 }} />
                  <Typography sx={{ pl: 1, fontSize: '14px', fontWeight: filterTab === tab.key ? 700 : 400 }}>{tab.label}</Typography>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>
        <Card>
          <Box sx={{ position: 'relative', height: calendarHeight }}>
            <CalendarStyle>
              <style>
                {`
                  .calendar-day-cell {
                    cursor: pointer !important;
                  }
                  .fc-daygrid-day {
                    cursor: pointer !important;
                  }
                  .fc .fc-daygrid-day-events {
                    margin-bottom: 2px;
                  }
                  .fc .fc-daygrid-more-link {
                    margin-top: 2px;
                    font-weight: 600;
                  }
                  @keyframes gradientAnimation {
                    0% {
                      background-position: 0 50%;
                    }
                    50% {
                      background-position: 100% 50%;
                    }
                    100% {
                      background-position: 0 50%;
                    }
                  }
                `}
              </style>
              <Box sx={{ position: 'relative' }}>
                <CalendarToolbar
                  date={date}
                  view={view}
                  onNextDate={handleClickDateNext}
                  onPrevDate={handleClickDatePrev}
                  onToday={handleClickToday}
                  onChangeView={handleChangeView}
                />
                {showDragIcon && (
                  <Tooltip title="Click and drag here to move calendar" arrow>
                    <IconButton
                      sx={{
                        position: 'absolute',
                        right: 190,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                        color: 'hsla(0, 0%, 100%, 0.9)',
                        width: 32,
                        height: 32,
                        borderRadius: '6px',
                        background: 'linear-gradient(-45deg, #ffa63d, #ff3d77, #338aff, #3cf0c5)',
                        backgroundSize: '600%',
                        animation: 'gradientAnimation 12s linear infinite',
                        '&:hover': {
                          transform: 'translateY(-50%) scale(1.1)',
                        },
                        zIndex: 10,
                        transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
                      }}
                      {...provided?.dragHandleProps}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <FullCalendar
                weekends
                editable
                droppable
                selectable
                events={events}
                ref={calendarRef}
                rerenderDelay={10}
                initialDate={date}
                initialView={view}
                dayMaxEvents={1}
                                eventDisplay="block"
                moreLinkClick="popover"
                                eventContent={(eventInfo) =>
                  renderEventContent(eventInfo, eventInfo.event._def.extendedProps.calendar_stage)
                }
                headerToolbar={false}
                allDayMaintainDuration
                select={handleSelectRange}
                dateClick={handleDateClick}
                eventDrop={handleDropEvent}
                eventClick={handleSelectEvent}
                datesSet={handleDatesSet}
                height={calendarHeight}
                plugins={[listPlugin, dayGridPlugin, timelinePlugin, timeGridPlugin, interactionPlugin]}
                dayCellClassNames="calendar-day-cell"
              />
            </CalendarStyle>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'rgba(13, 26, 36, 0.77)',
                  backdropFilter: 'blur(2px)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Card>

        {open && projectSelect ? (
          <EstimateCalendarPopOver
            key={projectSelect.id}
            anchorOrigin={anchorOrigin}
            title="Estimate Details"
            setOpen={setOpen}
            project={projectSelect}
            open={open}
            handleClose={() => {
              setOpen(false);
              setProjectSelect(null);
            }}
            refresh={refresh}
            setRefresh={setRefresh}
            embeddedInPcb={!!provided}
          />
        ) : null}

        <DateEstimatesPopup
          embeddedInPcb={!!provided}
          open={openDatePopup}
          onClose={() => setOpenDatePopup(false)}
          selectedDate={selectedDate}
          estimates={dateEstimates}
          onRefresh={() => {
            setRefresh(!refresh);
            getAllEstimates(currentMonth);
          }}
        />
        <ProductsPopover
          productNames={productsPopoverData.productNames}
          open={productsPopoverOpen}
          anchorEl={productsPopoverAnchor}
          onClose={() => setProductsPopoverOpen(false)}
          eventColor={productsPopoverData.eventColor}
        />
        <ServicesPopover
          serviceNames={servicesPopoverData.serviceNames}
          open={servicesPopoverOpen}
          anchorEl={servicesPopoverAnchor}
          onClose={() => setServicesPopoverOpen(false)}
          eventColor={servicesPopoverData.eventColor}
        />
      </Container>
    </Page>
  );
}
